import type { BackofficeUser, UserPermission } from '../types'
import { adminRequest, getCSRFToken, onAdminSessionExpired, setCSRFToken } from './adminTransport'

export interface AuthUser {
  user: BackofficeUser
  permissions: Record<string, boolean>
}

type SessionResponse = AuthUser & { csrfToken: string }

export function isValidNewPassword(password: string): boolean {
  const bytes = new TextEncoder().encode(password).length
  return password.length >= 12 && bytes <= 72
}

class AuthService {
  private currentUser: AuthUser | null = null
  private sessionTimeoutId: ReturnType<typeof setTimeout> | null = null

  constructor() {
    // Retire browser-readable credentials from the previous implementation.
    sessionStorage.removeItem('admin_session_token')
    sessionStorage.removeItem('csrf_token')
    onAdminSessionExpired(() => this.clearSession())
  }

  private acceptSession(session: SessionResponse): void {
    this.currentUser = { user: session.user, permissions: session.permissions }
    setCSRFToken(session.csrfToken)
    if (this.sessionTimeoutId) clearTimeout(this.sessionTimeoutId)
    this.sessionTimeoutId = setTimeout(() => {
      void this.logout().catch(() => this.clearSession())
    }, 30 * 60 * 1000)
  }

  private clearSession(): void {
    if (this.sessionTimeoutId) clearTimeout(this.sessionTimeoutId)
    this.sessionTimeoutId = null
    this.currentUser = null
    setCSRFToken(null)
  }

  async loadUserFromSession(): Promise<boolean> {
    try {
      this.acceptSession(await adminRequest<SessionResponse>('/session'))
      return true
    } catch {
      this.clearSession()
      return false
    }
  }

  async login(username: string, password: string): Promise<AuthUser | null> {
    this.clearSession()
    try {
      this.acceptSession(await adminRequest<SessionResponse>('/login', {
        method: 'POST', body: JSON.stringify({ username, password })
      }))
      return this.currentUser
    } catch {
      return null
    }
  }

  getCSRFToken(): string | null { return getCSRFToken() }
  validateCSRFToken(token: string): boolean { return !!token && token === getCSRFToken() }

  async logout(): Promise<void> {
    try {
      await adminRequest('/logout', { method: 'POST', body: '{}' })
    } finally {
      this.clearSession()
    }
  }

  getCurrentUser(): AuthUser | null { return this.currentUser }
  hasPermission(permission: string): boolean { return this.currentUser?.permissions[permission] === true }
  isAuthenticated(): boolean { return this.currentUser !== null }
  isAdmin(): boolean { return this.currentUser?.user.role === 'admin' }

  async createUser(userData: {
    username: string; email: string; password: string; full_name: string
    role: 'admin' | 'guide' | 'manager'; guide_id?: string
  }): Promise<BackofficeUser | null> {
    if (!this.isAdmin()) throw new Error('Unauthorized')
    try {
      return await adminRequest<BackofficeUser>('/users', { method: 'POST', body: JSON.stringify(userData) })
    } catch { return null }
  }

  async updateUserPermissions(userId: string, permissions: Record<string, boolean>): Promise<boolean> {
    if (!this.isAdmin()) throw new Error('Unauthorized')
    return this.updateUser(userId, 'permissions', { permissions })
  }

  async getAllUsers(): Promise<(BackofficeUser & { permissions: UserPermission[], guide?: { name: string } })[]> {
    if (!this.isAdmin()) throw new Error('Unauthorized')
    return adminRequest('/users')
  }

  async toggleUserStatus(userId: string): Promise<boolean> {
    if (!this.isAdmin()) throw new Error('Unauthorized')
    return this.updateUser(userId, 'status', {})
  }

  private async updateUser(userId: string, operation: string, data: object): Promise<boolean> {
    try {
      const result = await adminRequest<{ success: boolean }>(`/users/${encodeURIComponent(userId)}/${operation}`, {
        method: 'PATCH', body: JSON.stringify(data)
      })
      return result.success
    } catch { return false }
  }

  async changePassword(userId: string, newPassword: string): Promise<boolean> {
    if (!this.isAdmin() && this.currentUser?.user.id !== userId) throw new Error('Unauthorized')
    const success = await this.updateUser(userId, 'password', { newPassword })
    if (success && this.currentUser?.user.id === userId) this.clearSession()
    return success
  }

  async changeOwnPassword(currentPassword: string, newPassword: string): Promise<boolean> {
    if (!this.currentUser) throw new Error('Not authenticated')
    const success = await this.updateUser(this.currentUser.user.id, 'password', { currentPassword, newPassword })
    if (success) this.clearSession()
    return success
  }
}

export const authService = new AuthService()
